import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Coins } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import { getAdvancePayments, createAdvancePayment, deleteAdvancePayment } from '../../services/api';

const AdvancePaymentSection = ({ employeeId, onPaymentChange }) => {
  const [payments, setPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [paidDays, setPaidDays] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employeeId) fetchPayments();
  }, [employeeId]);

  const fetchPayments = async () => {
    try {
      const res = await getAdvancePayments(employeeId);
      setPayments(res.data || []);
    } catch { /* ignore */ }
  };

  const handleAdd = async () => {
    if (!paidDays || parseFloat(paidDays) <= 0) {
      toast({ title: 'Error', description: 'Enter valid paid days', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await createAdvancePayment(employeeId, {
        year: parseInt(year),
        paid_days: parseFloat(paidDays),
        notes
      });
      toast({ title: 'Saved', description: `Advance payment recorded for ${year}` });
      setShowForm(false);
      setPaidDays('');
      setNotes('');
      fetchPayments();
      if (onPaymentChange) onPaymentChange();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    }
    setLoading(false);
  };

  const handleDelete = async (yr) => {
    if (!window.confirm(`Remove advance payment for ${yr}?`)) return;
    try {
      await deleteAdvancePayment(employeeId, yr);
      toast({ title: 'Deleted', description: `Advance payment for ${yr} removed` });
      fetchPayments();
      if (onPaymentChange) onPaymentChange();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  if (!employeeId) return null;

  return (
    <div className="mt-4" data-testid="advance-payment-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <Coins className="w-3.5 h-3.5" />
          Advance Leave Payments
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setShowForm(!showForm)}
          data-testid="toggle-advance-form-btn"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add
        </Button>
      </div>

      {payments.length > 0 ? (
        <div className="space-y-1">
          {payments.map((p) => (
            <div key={p.year} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-xs" data-testid={`advance-payment-${p.year}`}>
              <div>
                <span className="font-medium">{p.year}</span>
                <span className="text-amber-700 ml-2">{p.paid_days} days paid by company</span>
                {p.notes && <span className="text-gray-500 ml-2">({p.notes})</span>}
              </div>
              <button onClick={() => handleDelete(p.year)} className="text-red-400 hover:text-red-600" data-testid={`delete-advance-${p.year}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No advance payments recorded. Excess leave defaults to LOP.</p>
      )}

      {showForm && (
        <div className="mt-2 p-3 border border-amber-200 rounded-lg bg-amber-50/50 space-y-2" data-testid="advance-payment-form">
          <p className="text-xs text-amber-700 font-medium">Record company-paid excess leave days</p>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Year</label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="h-8 text-xs" data-testid="advance-year-input" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500">Paid Days</label>
              <Input type="number" step="0.5" value={paidDays} onChange={(e) => setPaidDays(e.target.value)} placeholder="e.g. 15" className="h-8 text-xs" data-testid="advance-days-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Notes (optional)</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Management approved" className="h-8 text-xs" data-testid="advance-notes-input" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={handleAdd} disabled={loading} data-testid="save-advance-btn">
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancePaymentSection;
